export type EConsentDetailsModel = {
  createdAt: string | null;
  updatedAt: Date | null;
  appointmentDateTime: string | null;
  hn: string;
  fullName: string | null;
  fullNameEn: string | null;
  fullNameTh: string | null;
  transactionId: string | null;
  templateName: string | null;
  templateLanguage: string | null;
  voidedReason: string | null;
  status: string | null;
  email: string | null;
  nationalID: string | null;
  passportNo: string | null;
  birthDate: string | null;
  eSignatureLink: string | null;
  patientSubmittedAt: string | null;
  witnessSubmittedAt: string | null;
  salesforceCaseAppointmentId: string | null;
  cancelledBy: string | null;
  channel: string | null;
  company: string | null;
  createdBy: string | null;
  delegateTo: string | null;
  delegatedAt: string | null;
  delegateHistories: {
    to: string;
    at: string;
  }[]
  note: string | null;
  urgentCase?: {
    flag: boolean;
    at?: string | null;
    by?: string | null;
  } | null;
  signer?: {
    physician?: Signer;
    patient?: Signer;
    witness?: Signer;
    witness2?: Signer;
    interpreter?: Signer;
  };
  locationLogon?: {
    id?: string | null;
    name?: string | null;
  } | null;
};

export type econsentColumnModel = {
  appointmentDateTime: string;
  remainingDays: number;
  hn: string;
  fullName: string;
  status: string;
  transactionId: string;
  templateName: string;
  salesforceCaseAppointmentId: string;
  email: string;
  nationalID: string;
  passportNo: string;
  birthDate: string;
  voidedReason: string;
  patientSubmittedAt: string;
  witnessSubmittedAt: string;
  channel: string;
  company: string;
  createdBy: string
  delegateTo: string;
  delegatedAt: string;
  delegateHistories: {
    to: string;
    at: string;
  }[]
  note: string;
  physician: SignerModel;
  patient: SignerModel;
  witness: SignerModel;
  interpreter: SignerModel;
};

export type econsentColumnSpecial = {
  eSignatureLink: 'eSignatureLink'
};

export enum StatusType {
  PENDING_TO_CREATE_TRANSACTION = 0,
  PENDING_PATIENT = 1,
  PENDING_WITNESS = 2,
  COMPLETED = 3,
  CANCELLED = 4,
  DECLINED = 5,
  EXPIRED = 6,
  PENDING_SIGNER = 7,
  PARTIAL_COMPLETED = 8,
  ALL = 9,
  UNDEFINED = 99
}

export enum ChannelType {
  NONE = 'None',
  SALEFORCE = 'Saleforce',
  IPS = 'IPS',
  TRAKCARE = 'Trakcare',
  ALL = 'All'
}

export enum CompanyType {
  NONE = 'None',
  BUMRUNGRAD = 'BH',
  VITALLIFE = 'VTL',
  ESPERANCE = 'AGR',
  ALL = 'All'
}

export interface ApiResponse {
  data: EConsentDetailsModel[]
  status: number
}

export interface ApiError {
  message: string
  status: number
}

export type CancellationReasonMaster = {
  reason: string;
  description: string;
  requestDetail: boolean;
}

export type Signer = {
  candidateId: string;
  name: string;
  status: SignerStatus;
  link: string;
};

export type SignerModel = {
  id: string;
  name: string;
  status: string;
  link?: string;
};

export enum SignerStatus {
  DELEGATING = "DELEGATING",
  PENDING = "PENDING_SIGNER",
  SIGNED = "SIGNED",
}

export enum ConsentType {
  General = "General",
  Clinical = "Clinical"
}

export enum TransactionDelegateAction {
  Replace = "replace",
  Alternate = "alternate"
}

export interface TransactionDelegateRequest {
  action?: TransactionDelegateAction;
  candidate: {
    id?: string;
    code?: string;
    username?: string;
    name: string;
    emailAddress: string;
  };
  type: ConsentType;
  additional?: {
    suffixSignerName?: string;
  };
}

/** Display labels for template language (En/Th). */
export enum Language {
  En = "English",
  Th = "Thai",
  Ar = "Arabic",
  Jp = "Japanese",
  Cn = "Chinese Mandarin",
  Mm = "Burmese",
  Kh = "Khmer",
}

export interface Employee {
  employeeName: string;
  employeeCode: string;
  employeeType: string;
  nameTH?: string;
  nameEN?: string;
  userName?: string;
}

export interface TransactionInfoSigner {
  candidateId: string;
  name: string;
  role: string;
  employeeCode: string;
  userName?: string;
  signerLink: string;
  signerStatus: string;
}

export interface TransactionInfo {
  hn: string;
  status: string;
  formName: string;
  signers: TransactionInfoSigner[];
  completedLink: string;
  createdBy: string;
  createdDate: string;
  isGeneralForm: boolean;
}

export type OnetrustStatusModel = {
  consentItem: ConsentItemModel[] | null;
}

export type ConsentItemModel = {
  purposeName: string | null;
  purposeActive: string | null;
  purposeLastUpdatedDate: Date | null;
}

export type VerifySignerResponse = { signerLink: string };

/// Dynamic form response

/** Default value for a single form field from API (e.g. statusOfSigner by patient age). */
export interface DynamicFormDefaultFieldValue {
  value: string;
  additional?: Record<string, unknown>;
}

export interface DynamicFormResponse {
  form: { code: string; name: string };
  template: { id: string; language: string; templateGroup: string; availableLanguages?: Language[]; };
  preparedBy: string;
  patientInfo: DynamicFormPatientInfo;
  fields: DynamicFormField[];
  rules: Record<string, DynamicFormRule>;
  participants: DynamicFormParticipant[];
  /** Default form values from API (e.g. statusOfSigner for child vs adult). */
  defaultValues?: Record<string, DynamicFormDefaultFieldValue> | null;
  locationLogon?: { id: string; name: string } | null;
  securityGroup?: { id: string; name: string } | null;
}

export interface DynamicFormPatientInfo {
  name: string;
  hn: string;
  birthDate: string;
  sex: string;
  age: string;
  wardRoom: string;
  allergie: string;
  preferredLanguage?: string;
}

export enum DynamicFormFieldType {
  Text = "text",
  Lookup = "lookup",
  // Autocomplete with MUI `freeSolo` — the submitted value can be text not in the options list
  LookupOrFreeText = "lookupOrFreeText",
  LookupWithConjunction = "lookupWithConjunction",
  LookupOrFreeTextWithConjunction = "lookupOrFreeTextWithConjunction",
  Hidden = "hidden",
  Checkbox = "checkbox",
}

export enum DynamicFormFieldRule {
  CustomRule = "customRule",
  Required = "required",
  RequiredIfChecked = "requiredIfChecked",
  AppearedIfChecked = "appearedIfChecked",
}

export interface DynamicFormField {
  name: string;
  label: string;
  type: string;
  order: number;
  rule: DynamicFormFieldRule | null;
  additional: DynamicFormFieldAdditional | null;
}

/** Lookup type for dynamic form fields (Doctor, Witness, Language, Interpreter, Anesthetist, Employee, StatusOfSigner, PatientInfoDocuments, etc.). */
export enum DynamicFormLookupType {
  Doctor = "Doctor",
  Witness = "Witness",
  Language = "Language",
  Interpreter = "Interpreter",
  Anesthetist = "Anesthetist",
  Employee = "Employee",
  StatusOfSigner = "StatusOfSigner",
  Conjunctions = "Conjunctions",
  PrimaryProcedures = "PrimaryProcedures",
  Procedures = "Procedures",
  PatientInfoDocuments = "PatientInfoDocuments",
  Registration = "Registration",
}

/** Employee/signer lookup types only: Doctor, Witness, Interpreter, Anesthetist, Employee. */
export type DynamicFormLookupTypeEmployee = keyof Pick<
  typeof DynamicFormLookupType,
  'Doctor' | 'Witness' | 'Interpreter' | 'Anesthetist' | 'Employee' | 'Registration'
>;

export interface DynamicFormFieldAdditional {
  lookupType?: DynamicFormLookupType | string;
  ruleName?: string;
  mutuallyExclusiveWith?: string[] | null;
  dynamicFieldName?: string;
  [key: string]: unknown;
}

export enum DynamicFormRuleBase {
  RequiredIfEqual = "requiredIfEqual",
  AppearedIfEqual = "appearedIfEqual",
}

export interface DynamicFormRule {
  base: DynamicFormRuleBase;
  condition: {
    field: string;
    path: string | null;
    value: unknown;
  } | null;
}

/** Field reference for candidate: where to read name/email/password from form values. */
export interface DynamicFormCandidateField {
  field: string;
  type: ParticipantPrefillerFieldType;
  path: string | null;
  format?: string | null;
}

export interface DynamicFormParticipant {
  sequence: number;
  role: string;
  fields: DynamicFormParticipantField[];
  placeholder?: string | null;
  candidates?: Record<string, DynamicFormCandidateField[]>[];
}

export enum ParticipantPrefillerFieldType {
  Text = "text",
  Date = "date",
  Signer = "signer",
  CheckByValue = "checkByValue",
}

export enum ParticipantFieldModeType {
  Replace = "replace",
  Combine = "combine",
}

export interface DynamicFormParticipantField {
  field: string;
  type: ParticipantPrefillerFieldType;
  format: string | null;
  path: string | null;
  role: string | null;
  mode: ParticipantFieldModeType;
  separator?: string;
  prefills: string[];
  previews: string[];
  pdfCoordinates: Record<string, PdfCoordinateItem[]> | null;
}

export interface PdfCoordinateItem {
  page: number;
  x: number;
  y: number;
  maxWidth: number;
  unit: number;
  fieldType: string;
  fieldName: string;
}

/** Form state: field name -> value and optional additional (for lookup: label, value, isNok, signerName, etc.) */
export interface DynamicFormValues {
  [fieldName: string]: {
    value: string;
    additional?: Record<string, unknown>;
  };
}

/** Unified option for dynamic form lookup (Doctor, Witness, Language, Interpreter, StatusOfSigner). */
export interface DynamicFormLookupOption {
  description: string;
  value: string;
  additional?: Record<string, unknown> | null;
}

/** Request body for creating a dynamic form transaction (Certinal). */
export interface CreateDynamicFormTransactionRequest {
  formCode?: string;
  language?: string;
  participants: {
    sequence: number;
    role: string;
    placeholder?: string | null;
    fields?: { name: string; value?: string | null }[];
    candidates?: Record<string, unknown>[];
  }[];
  locationLogon?: { id?: string; name?: string } | null;
  securityGroup?: { id?: string; name?: string } | null;
}

/** Response shape for create dynamic form transaction (API returns DTO). */
export interface CreateDynamicFormTransactionResponse {
  transactionId: string;
}

export type UserLocationOption = {
  code: string;
  description: string;
};

export type SwitchLocationSession = {
  token: string;
  refreshToken: string;
  expiryInSecond: number;
  promptExpiryInSecond: number;
};
